'use client';
import React, { useState, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import interactionLedger from '../../utils/interactionLedger';

const UserLogs = () => {
  const [logs, setLogs] = useState([]);
  const [filterType, setFilterType] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState('newest');
  const [selectedLog, setSelectedLog] = useState(null);
  const [stats, setStats] = useState(null);

  // Refresh logs from ledger
  const refreshLogs = () => {
    const allLogs = interactionLedger.getAll();
    setLogs([...allLogs]);
    setStats(interactionLedger.getStats());
  };

  // Initial load and set up refresh interval
  useEffect(() => {
    refreshLogs();
    const interval = setInterval(refreshLogs, 1000);
    return () => clearInterval(interval);
  }, []);

  // Filter and sort logs
  const filteredAndSortedLogs = useMemo(() => {
    let filtered = logs;

    if (filterType) {
      filtered = filtered.filter(log => log.type === filterType);
    }

    if (filterCategory) {
      filtered = filtered.filter(log => log.category === filterCategory);
    }

    if (searchQuery) {
      const lowerQuery = searchQuery.toLowerCase();
      filtered = filtered.filter(log => {
        return (
          log.description.toLowerCase().includes(lowerQuery) ||
          JSON.stringify(log.data).toLowerCase().includes(lowerQuery) ||
          log.type.toLowerCase().includes(lowerQuery) ||
          log.category.toLowerCase().includes(lowerQuery)
        );
      });
    }

    const sorted = [...filtered].sort((a, b) => {
      if (sortOrder === 'newest') {
        return b.time - a.time;
      } else {
        return a.time - b.time;
      }
    });

    return sorted;
  }, [logs, filterType, filterCategory, searchQuery, sortOrder]);

  // Get unique types and categories for filters
  const uniqueTypes = useMemo(() => {
    const types = new Set(logs.map(log => log.type));
    return Array.from(types).sort();
  }, [logs]);

  const uniqueCategories = useMemo(() => {
    const categories = new Set(logs.map(log => log.category));
    return Array.from(categories).sort();
  }, [logs]);

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  // Format relative time
  const formatRelativeTime = (time) => {
    const now = Date.now();
    const diff = now - time;
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (seconds < 60) return `${seconds}s ago`;
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return formatTimestamp(time);
  };

  // Clear logs
  const handleClear = () => {
    if (confirm('Are you sure you want to clear all logs?')) {
      interactionLedger.clear();
      refreshLogs();
      setSelectedLog(null);
    }
  };

  // Export logs
  const handleExport = () => {
    const exportData = interactionLedger.export();
    const blob = new Blob([exportData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `user-logs-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Get log type color
  const getLogTypeColor = (type) => {
    const colors = {
      click: '#2196F3',
      stateChange: '#4CAF50',
      urlChange: '#FF9800',
      apiError: '#F44336',
      error: '#F44336',
      noteSelection: '#9C27B0',
      noteOpen: '#00897B',
      noteOpenError: '#E53935',
      noteSync_mismatch: '#FF1744',
      stale_note_save: '#FF6D00',
      panelSelection: '#00BCD4',
      panelLayout: '#607D8B',
      default: '#757575',
    };
    return colors[type] || colors.default;
  };

  return (
    <div className="user-logs-container" style={{ 
      display: 'grid', 
      gridTemplateRows: 'auto 1fr', 
      height: '100%', 
      backgroundColor: '#1e1e1e',
      color: '#cccccc',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    }}>
      {/* Modern Header */}
      <div className="user-logs-header" style={{ 
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        gap: '1.5rem',
        padding: '1.5rem 2rem',
        borderBottom: '1px solid #3c3c3c',
        backgroundColor: '#252526',
        alignItems: 'center',
      }}>
        <div style={{ display: 'grid', gap: '0.75rem' }}>
          <h1 style={{ 
            margin: 0, 
            fontSize: '1.5rem', 
            fontWeight: 600,
            color: '#ffffff',
            letterSpacing: '-0.01em',
          }}>
            User Logs
          </h1>
          {stats && (
            <div style={{ 
              fontSize: '0.875rem', 
              color: '#888888',
              display: 'grid',
              gridAutoFlow: 'column',
              gap: '0.5rem',
              alignItems: 'center',
            }}>
              <span style={{ color: '#4CAF50', fontWeight: 500 }}>{stats.total}</span>
              <span>total</span>
              <span>•</span>
              <span style={{ color: '#2196F3', fontWeight: 500 }}>{filteredAndSortedLogs.length}</span>
              <span>showing</span>
            </div>
          )}
        </div>
        <div style={{ display: 'grid', gridAutoFlow: 'column', gap: '0.75rem', alignItems: 'center' }}>
          <button
            onClick={handleClear}
            style={{ 
              padding: '0.625rem 1.25rem', 
              backgroundColor: 'transparent', 
              color: '#f44336', 
              border: '1px solid #f44336', 
              borderRadius: '6px', 
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: 500,
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#f44336';
              e.target.style.color = '#ffffff';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = '#f44336';
            }}
          >
            Clear
          </button>
          <button
            onClick={handleExport}
            style={{ 
              padding: '0.625rem 1.25rem', 
              backgroundColor: '#007acc', 
              color: '#ffffff', 
              border: 'none', 
              borderRadius: '6px', 
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: 500,
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#005a9e';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#007acc';
            }}
          >
            Export
          </button>
        </div>
      </div>

      {/* Filters Bar */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        gap: '1rem',
        padding: '1rem 2rem',
        borderBottom: '1px solid #3c3c3c',
        backgroundColor: '#252526',
        alignItems: 'center',
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '0.75rem' }}>
          <input
            type="text"
            placeholder="Search logs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ 
              padding: '0.625rem 1rem', 
              border: '1px solid #3c3c3c', 
              borderRadius: '6px',
              backgroundColor: '#1e1e1e',
              color: '#cccccc',
              fontSize: '0.875rem',
              outline: 'none',
              transition: 'all 0.2s ease',
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#007acc';
              e.target.style.boxShadow = '0 0 0 2px rgba(0, 122, 204, 0.2)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#3c3c3c';
              e.target.style.boxShadow = 'none';
            }}
          />
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            style={{ 
              padding: '0.625rem 1rem', 
              border: '1px solid #3c3c3c', 
              borderRadius: '6px',
              backgroundColor: '#1e1e1e',
              color: '#cccccc',
              fontSize: '0.875rem',
              outline: 'none',
              cursor: 'pointer',
            }}
          >
            <option value="">All Types</option>
            {uniqueTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            style={{ 
              padding: '0.625rem 1rem', 
              border: '1px solid #3c3c3c', 
              borderRadius: '6px',
              backgroundColor: '#1e1e1e',
              color: '#cccccc',
              fontSize: '0.875rem',
              outline: 'none',
              cursor: 'pointer',
            }}
          >
            <option value="">All Categories</option>
            {uniqueCategories.map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>
          <select
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            style={{ 
              padding: '0.625rem 1rem', 
              border: '1px solid #3c3c3c', 
              borderRadius: '6px',
              backgroundColor: '#1e1e1e',
              color: '#cccccc',
              fontSize: '0.875rem',
              outline: 'none',
              cursor: 'pointer',
            }}
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
          </select>
        </div>
      </div>

      {/* Main content area */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: selectedLog ? '1fr 400px' : '1fr', 
        gap: 0,
        overflow: 'hidden', 
        height: '100%',
        backgroundColor: '#1e1e1e',
      }}>
        {/* Logs list */}
        <div style={{ 
          overflowY: 'auto', 
          borderRight: selectedLog ? '1px solid #3c3c3c' : 'none', 
          height: '100%',
          padding: '1rem',
        }}>
          {filteredAndSortedLogs.length === 0 ? (
            <div style={{ 
              padding: '4rem 2rem', 
              textAlign: 'center', 
              color: '#888888',
              display: 'grid',
              gap: '0.5rem',
            }}>
              <div style={{ fontSize: '3rem', opacity: 0.3 }}>📋</div>
              <div style={{ fontSize: '1.125rem', fontWeight: 500, color: '#cccccc' }}>
                {logs.length === 0 ? 'The ledger is empty' : 'No logs match your filters'}
              </div>
              <div style={{ fontSize: '0.875rem' }}>
                {logs.length === 0 ? 'Start interacting with the app to see logs here' : 'Try adjusting your search or filters'}
              </div>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '0.75rem' }}>
              {filteredAndSortedLogs.map((log, index) => (
                <div
                  key={`${log.time}-${index}`}
                  onClick={() => setSelectedLog(log)}
                  style={{
                    padding: '1rem',
                    border: `1px solid ${selectedLog?.time === log.time ? '#007acc' : '#3c3c3c'}`,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    backgroundColor: selectedLog?.time === log.time ? '#1a3a52' : '#252526',
                    display: 'grid',
                    gridTemplateColumns: 'auto 1fr auto',
                    gap: '1rem',
                    alignItems: 'start',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    if (selectedLog?.time !== log.time) {
                      e.currentTarget.style.backgroundColor = '#2d2d30';
                      e.currentTarget.style.borderColor = '#3c3c3c';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedLog?.time !== log.time) {
                      e.currentTarget.style.backgroundColor = '#252526';
                      e.currentTarget.style.borderColor = '#3c3c3c';
                    }
                  }}
                >
                  <div
                    style={{
                      width: '10px',
                      height: '10px',
                      borderRadius: '50%',
                      backgroundColor: getLogTypeColor(log.type),
                      marginTop: '0.375rem',
                      boxShadow: `0 0 0 3px ${getLogTypeColor(log.type)}33`,
                    }}
                  />
                  <div style={{ flex: 1, minWidth: 0, display: 'grid', gap: '0.5rem' }}>
                    <div style={{ 
                      fontWeight: 500, 
                      fontSize: '0.9375rem',
                      color: '#ffffff',
                      lineHeight: 1.4,
                    }}>
                      {log.description || log.type}
                    </div>
                    <div style={{ 
                      fontSize: '0.8125rem', 
                      color: '#888888', 
                      display: 'grid',
                      gridAutoFlow: 'column',
                      gap: '0.5rem',
                      alignItems: 'center',
                    }}>
                      <span style={{ 
                        padding: '0.125rem 0.5rem',
                        backgroundColor: '#37373d',
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                      }}>
                        {log.type}
                      </span>
                      <span style={{ color: '#555555' }}>•</span>
                      <span>{log.category}</span>
                    </div>
                  </div>
                  <div style={{ 
                    fontSize: '0.8125rem', 
                    color: '#888888', 
                    whiteSpace: 'nowrap',
                    paddingTop: '0.125rem',
                  }}>
                    {formatRelativeTime(log.time)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Log details panel */}
        {selectedLog && (
          <div style={{ 
            overflowY: 'auto', 
            padding: '1.5rem', 
            height: '100%',
            backgroundColor: '#252526',
            borderLeft: '1px solid #3c3c3c',
          }}>
            <div style={{ 
              marginBottom: '1.5rem', 
              display: 'grid', 
              gridTemplateColumns: '1fr auto', 
              gap: '1rem', 
              alignItems: 'start',
              paddingBottom: '1.5rem',
              borderBottom: '1px solid #3c3c3c',
            }}>
              <div style={{ display: 'grid', gap: '0.75rem' }}>
                <h3 style={{ 
                  margin: 0, 
                  fontSize: '1.125rem',
                  fontWeight: 600,
                  color: '#ffffff',
                }}>
                  {selectedLog.description || selectedLog.type}
                </h3>
                <div style={{ 
                  fontSize: '0.875rem', 
                  color: '#888888', 
                  display: 'grid',
                  gridAutoFlow: 'column',
                  gap: '0.75rem',
                  alignItems: 'center',
                }}>
                  <span style={{ 
                    padding: '0.25rem 0.625rem',
                    backgroundColor: '#37373d',
                    borderRadius: '4px',
                    fontSize: '0.8125rem',
                  }}>
                    {selectedLog.type}
                  </span>
                  <span style={{ color: '#555555' }}>•</span>
                  <span>{selectedLog.category}</span>
                </div>
              </div>
              <button
                onClick={() => setSelectedLog(null)}
                style={{ 
                  padding: '0.5rem', 
                  backgroundColor: 'transparent', 
                  border: '1px solid #3c3c3c', 
                  borderRadius: '6px', 
                  cursor: 'pointer',
                  color: '#cccccc',
                  fontSize: '1.125rem',
                  lineHeight: 1,
                  width: '32px',
                  height: '32px',
                  display: 'grid',
                  placeItems: 'center',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#37373d';
                  e.target.style.borderColor = '#555555';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.borderColor = '#3c3c3c';
                }}
              >
                ✕
              </button>
            </div>

            <div style={{ marginBottom: '1.5rem', display: 'grid', gap: '0.5rem' }}>
              <div style={{ fontSize: '0.8125rem', color: '#888888', fontWeight: 500 }}>
                Timestamp
              </div>
              <div style={{ 
                fontSize: '0.875rem', 
                color: '#cccccc',
                fontFamily: 'monospace',
                padding: '0.75rem',
                backgroundColor: '#1e1e1e',
                borderRadius: '6px',
                border: '1px solid #3c3c3c',
              }}>
                {formatTimestamp(selectedLog.timestamp || selectedLog.time)}
              </div>
            </div>

            {selectedLog.data && Object.keys(selectedLog.data).length > 0 && (
              <div style={{ marginBottom: '1.5rem', display: 'grid', gap: '0.5rem' }}>
                <div style={{ fontSize: '0.8125rem', color: '#888888', fontWeight: 500 }}>
                  Data
                </div>
                <pre style={{ 
                  backgroundColor: '#1e1e1e', 
                  padding: '1rem', 
                  borderRadius: '6px', 
                  overflow: 'auto',
                  fontSize: '0.8125rem',
                  margin: 0,
                  border: '1px solid #3c3c3c',
                  color: '#cccccc',
                  fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
                  lineHeight: 1.6,
                }}>
                  {JSON.stringify(selectedLog.data, null, 2)}
                </pre>
              </div>
            )}

            {selectedLog.stackTrace && selectedLog.stackTrace.length > 0 && (
              <div style={{ marginBottom: '1.5rem', display: 'grid', gap: '0.5rem' }}>
                <div style={{ fontSize: '0.8125rem', color: '#888888', fontWeight: 500 }}>
                  Stack Trace
                </div>
                <pre style={{ 
                  backgroundColor: '#1e1e1e', 
                  padding: '1rem', 
                  borderRadius: '6px', 
                  overflow: 'auto',
                  fontSize: '0.75rem',
                  margin: 0,
                  border: '1px solid #3c3c3c',
                  color: '#cccccc',
                  fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
                  lineHeight: 1.6,
                  maxHeight: '200px',
                }}>
                  {selectedLog.stackTrace.join('\n')}
                </pre>
              </div>
            )}

            <div style={{ 
              marginTop: '1.5rem', 
              paddingTop: '1.5rem', 
              borderTop: '1px solid #3c3c3c',
              display: 'grid',
              gap: '0.5rem',
            }}>
              <div style={{ fontSize: '0.8125rem', color: '#888888', fontWeight: 500 }}>
                Raw JSON
              </div>
              <pre style={{ 
                backgroundColor: '#1e1e1e', 
                padding: '1rem', 
                borderRadius: '6px', 
                overflow: 'auto',
                fontSize: '0.75rem',
                margin: 0,
                border: '1px solid #3c3c3c',
                color: '#cccccc',
                fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
                lineHeight: 1.6,
                maxHeight: '300px',
              }}>
                {JSON.stringify(selectedLog, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserLogs;
